import React from 'react';
import { Link } from 'react-router-dom';
import {
  PetlingsLogo,
  PersonalityIcon,
  CosmeticsIcon,
  ChatIcon,
  KennelIcon,
  PetCountIcon,
  SyncIcon,
  StarIcon,
  HeartIcon,
  FoodIcon,
  EnergyIcon,
  LeafIcon,
  ArrowIcon,
} from '../components/icons';
import { PetVisual, ViewModeToggle, petInfo, PETS, TIER_LABELS, usePetSelection } from '../components/visuals';
import { RELEASE_NAME, RELEASE_VERSION } from '../components/release/releaseCatalog';
import DownloadDropdown from '../components/DownloadDropdown';
import PetDivider from '../components/PetDivider';
import './About.css';

/** The companions the About page shows; the rest are a click away. */
const ABOUT_PETS = ['cat', 'wolf', 'fox', 'panda', 'axolotl', 'baby_dragon', 'slime'];

function ShieldCheckIcon({ size = 24, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 2L3 7V12C3 17.5 7 21.5 12 23C17 21.5 21 17.5 21 12V7L12 2Z" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.08" />
      <path d="M9 12L11 14L15 10" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function GlobeIcon({ size = 24, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.5" />
      <path d="M3 12H21M12 3C14.5 6 15.5 9 15.5 12C15.5 15 14.5 18 12 21M12 3C9.5 6 8.5 9 8.5 12C8.5 15 9.5 18 12 21" stroke={color} strokeWidth="1.3" />
    </svg>
  );
}

function LightbulbIcon({ size = 24, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M9 21H15M12 2C8.7 2 6 4.7 6 8C6 10.4 7.5 12.5 9.6 13.5C10 13.7 10 14 10 14.5V16H14V14.5C14 14 14 13.7 14.4 13.5C16.5 12.5 18 10.4 18 8C18 4.7 15.3 2 12 2Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M10 18H14" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export default function About() {
  const aboutPets = usePetSelection(ABOUT_PETS);
  return (
    <div className="about-page">
      {/* Hero */}
      <section className="about-hero">
        <div className="container about-hero-inner">
          <PetlingsLogo size={48} color="var(--magenta-primary)" />
          <h1 className="about-hero-title">
            Your tiny companion,<br />
            <span className="about-hero-highlight">in every world.</span>
          </h1>
          <p className="about-hero-subtitle">
            Petlings is a client-side Minecraft mod that gives every player an AI-powered
            friend that follows you, talks to you, remembers your adventures,
            and grows alongside you.
          </p>
          <div className="about-hero-meta">
            <span className="about-hero-pill">v{RELEASE_VERSION} - {RELEASE_NAME}</span>
            <span className="about-hero-dot" aria-hidden="true">.</span>
            <span>MC 1.21.11 &amp; 26.1</span>
            <span className="about-hero-dot" aria-hidden="true">.</span>
            <span>Fabric 0.16+</span>
          </div>
          <div className="about-hero-links">
            <a href="https://modrinth.com/mod/petlings" target="_blank" rel="noopener noreferrer" className="about-hero-link">Modrinth</a>
            <a href="https://www.curseforge.com/minecraft/mc-mods/petlings" target="_blank" rel="noopener noreferrer" className="about-hero-link">CurseForge</a>
            <a href="https://discord.gg/hkcNBCTckG" target="_blank" rel="noopener noreferrer" className="about-hero-link">Discord</a>
            <a href="https://github.com/Bownlux/Petlings" target="_blank" rel="noopener noreferrer" className="about-hero-link">GitHub</a>
          </div>
        </div>
      </section>

      <PetDivider />

      {/* Why Petlings - the story */}
      <section className="about-story">
        <div className="container">
          <div className="about-story-card card">
            <div className="about-story-icon">
              <LeafIcon size={28} color="var(--green-dark)" />
            </div>
            <h2>Why we built Petlings</h2>
            <p>
              We believe that exploring Minecraft with a companion by your side is part of the
              fun and growth. Whether you're deep in a cave, building at your base, or sailing
              across oceans, your petling is right there with you.
            </p>
            <p>
              Every pet has a personality. Every conversation is unique. And when you can't log
              into Minecraft, you can still check on your pets from the web to feed them, dress
              them up, and chat with them from anywhere.
            </p>
          </div>
        </div>
      </section>

      {/* Cross-platform sync showcase */}
      <section className="about-sync">
        <div className="container">
          <h2 className="section-title">One pet, everywhere</h2>
          <p className="section-subtitle">
            Your pets live in the cloud. Change something on the web, see it in-game. And vice versa.
          </p>

          <div className="sync-demo">
            {/* Web side */}
            <div className="sync-panel sync-web">
              <div className="sync-panel-header">
                <GlobeIcon size={16} color="var(--magenta-primary)" />
                <span>petlings.gg</span>
              </div>
              <div className="sync-panel-body">
                <div className="sync-pet-preview">
                  {/* The web shows the art... */}
                  <PetVisual petId="shadowling_wolf" size={88} forceMode="2d" />
                </div>
                <div className="sync-action">Equip top hat</div>
                <div className="sync-action">Feed treats</div>
                <div className="sync-action">Chat: "Let's go mining!"</div>
              </div>
            </div>

            {/* Sync arrows */}
            <div className="sync-arrows">
              <div className="sync-arrow sync-arrow-right">
                <SyncIcon size={28} color="var(--magenta-primary)" />
              </div>
              <span className="sync-label">Real-time sync</span>
            </div>

            {/* Minecraft side */}
            <div className="sync-panel sync-mc">
              <div className="sync-panel-header">
                <LeafIcon size={16} color="var(--green-primary)" />
                <span>Minecraft</span>
              </div>
              <div className="sync-panel-body">
                <div className="sync-pet-preview">
                  {/* ...and Minecraft the same pet in 3D. */}
                  <PetVisual petId="shadowling_wolf" size={88} forceMode="3d" />
                </div>
                <div className="sync-result">Hat appears on pet</div>
                <div className="sync-result">Hunger bar fills up</div>
                <div className="sync-result">Pet responds in chat</div>
              </div>
            </div>
          </div>

          <p className="sync-note">
            Add a hat from your phone, it appears in-game within seconds.
            Care for your pet from anywhere and it'll be happy when you log back in.
          </p>
        </div>
      </section>

      {/* AI Chat showcase */}
      <section className="about-chat">
        <div className="container">
          <div className="chat-showcase">
            <div className="chat-showcase-convo">
              <h3>Your pet actually talks to you.</h3>
              <div className="chat-bubble chat-user">How do I find a nether fortress?</div>
              <div className="chat-bubble chat-pet">
                <span className="chat-pet-name">Whiskers</span>
                *perks ears* Travel along the Z axis in the Nether! Fortresses generate in strips. I remember we found one near that lava lake yesterday - want to head back there?
              </div>
              <div className="chat-bubble chat-user">Let's go!</div>
              <div className="chat-bubble chat-pet">
                <span className="chat-pet-name">Whiskers</span>
                *bounces excitedly* Adventure time! I'll keep my eyes peeled for blaze spawners. Don't forget your fire resistance potions!
              </div>
            </div>
            <div className="chat-showcase-info">
              <ChatIcon size={32} color="var(--magenta-primary)" />
              <h3>AI-powered conversations</h3>
              <p>
                Every pet has a unique personality shaped by its traits. They remember
                your conversations, give Minecraft tips, react to your mood, and plan
                adventures with you. Press K in-game to open quick chat anytime.
              </p>
              <div className="chat-tags">
                <span>Remembers your travels</span>
                <span>Crafting recipes</span>
                <span>Mob strategies</span>
                <span>Unique personality</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature deep-dive */}
      <section className="about-features">
        <div className="container">
          <h2 className="section-title">Everything your pet can do</h2>

          <div className="about-feature-grid">
            <div className="about-feature-card card">
              <div className="about-feature-icon">
                <PersonalityIcon size={32} color="var(--magenta-primary)" />
              </div>
              <h3>15 Personality Traits</h3>
              <p>Playful, curious, brave, lazy, mischievous, wise. Each pet gets 3 random traits that shape how they talk, react, and behave.</p>
            </div>
            <div className="about-feature-card card">
              <div className="about-feature-icon">
                <CosmeticsIcon size={32} color="var(--magenta-primary)" />
              </div>
              <h3>150+ Cosmetics</h3>
              <p>Hats, glasses, capes, collars, boots, weapons, wings, trails. 3D models that attach to your pet's body, with animated pieces like storm halos, clockwork gears, and propeller packs.</p>
            </div>
            <div className="about-feature-card card">
              <div className="about-feature-icon">
                <PetCountIcon size={32} color="var(--magenta-primary)" />
              </div>
              <h3>42 Pet Species</h3>
              <p>Cats, wolves, foxes, rabbits, parrots, pandas, axolotls, bees, dragons, elemental slimes, wisps, iron golems, and more.</p>
            </div>
            <div className="about-feature-card card">
              <div className="about-feature-icon">
                <KennelIcon size={32} color="#8D6E63" />
              </div>
              <h3>Your Kennel</h3>
              <p>Adopt up to 10 pets. Each has its own stats, cosmetics, chat history, and level. Switch between them anytime.</p>
            </div>
            <div className="about-feature-card card">
              <div className="about-feature-icon">
                <HeartIcon size={32} color="#E57373" />
              </div>
              <h3>Tamagotchi Care</h3>
              <p>Feed, play, and rest. Your pet has hunger, happiness, and energy needs. Neglect them and they'll get sad. Care for them and they'll thrive.</p>
            </div>
            <div className="about-feature-card card">
              <div className="about-feature-icon">
                <StarIcon size={32} color="var(--yellow-warm)" />
              </div>
              <h3>6 Abilities, 19 Emotes</h3>
              <p>Level up to unlock 6 active abilities like night vision, ore radar, and combat buddy. Express yourself with 19 emotes on a quick-access wheel.</p>
            </div>
            <div className="about-feature-card card">
              <div className="about-feature-icon">
                <SyncIcon size={32} color="var(--magenta-primary)" />
              </div>
              <h3>Fully Multiplayer</h3>
              <p>Your pet works on any server, vanilla or modded. Other players with the mod see your pet, your cosmetics, and your emotes in real time. No server plugins needed.</p>
            </div>
            <div className="about-feature-card card">
              <div className="about-feature-icon">
                <GlobeIcon size={32} color="var(--magenta-primary)" />
              </div>
              <h3>Play Anywhere</h3>
              <p>Care for your pets from the web or in Minecraft today, with iOS coming soon. Feed them, dress them up, and chat with them from anywhere. Everything syncs instantly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pet abilities showcase */}
      <section className="about-abilities">
        <div className="container">
          <h2 className="section-title">Your pet helps you play</h2>
          <p className="section-subtitle">As your pet levels up, it unlocks abilities that make exploring easier.</p>

          <div className="ability-grid">
            <div className="ability-card">
              <LightbulbIcon size={24} color="var(--yellow-warm)" />
              <div>
                <h4>Night Vision (Lv. 4)</h4>
                <p>Your pet glows and lights up dark caves so you can see without torches.</p>
              </div>
            </div>
            <div className="ability-card">
              <EnergyIcon size={24} color="var(--magenta-primary)" />
              <div>
                <h4>Ore Radar (Lv. 5)</h4>
                <p>Pet sniffs out nearby ores and highlights them with particles.</p>
              </div>
            </div>
            <div className="ability-card">
              <ArrowIcon size={24} color="var(--magenta-primary)" />
              <div>
                <h4>Swift Companion (Lv. 6)</h4>
                <p>Gives you Speed I while your pet is following you.</p>
              </div>
            </div>
            <div className="ability-card">
              <FoodIcon size={24} color="var(--orange-primary)" />
              <div>
                <h4>Pet Satchel (Lv. 8)</h4>
                <p>Your pet carries a 6-slot inventory. Extra storage on the go.</p>
              </div>
            </div>
            <div className="ability-card">
              <ShieldCheckIcon size={24} color="#E57373" />
              <div>
                <h4>Combat Buddy (Lv. 10)</h4>
                <p>Pet fights hostile mobs alongside you with unique attack animations.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pet showcase strip */}
      <section className="about-pets">
        <div className="container">
          <h2 className="section-title">Meet the companions</h2>
          <div className="about-pets-toggle"><ViewModeToggle compact /></div>
          <div className="about-pet-strip">
            {aboutPets.map((id) => {
              const pet = petInfo(id);
              return (
                <Link key={id} to="/catalog" className="about-pet-card">
                  <PetVisual petId={id} size={72} mood="happy" />
                  <span className="about-pet-name">{pet.name}</span>
                  <span className={`tier-badge tier-${pet.tier}`}>{TIER_LABELS[pet.tier]}</span>
                </Link>
              );
            })}
            <Link to="/catalog" className="about-pet-card about-pet-more">
              <span className="about-pet-more-count">+{PETS.length - ABOUT_PETS.length}</span>
              <span className="about-pet-name">More</span>
            </Link>
          </div>
        </div>
      </section>

      {/* What's new in this update */}
      <section className="about-changelog">
        <div className="container">
          <div className="about-changelog-card card">
            <span className="about-changelog-badge">New in {RELEASE_VERSION} - {RELEASE_NAME}</span>
            <h2>A new look, and gardens to grow</h2>
            <p>
              Every Petling has been redrawn in the new voxel style, in 2D and in 3D, with
              moods, new animations and cosmetics to match: tap a pet on its page here and it
              waves back. In the Petlings app all your pets share your gardens: tidy the soil,
              bloom it, grant its wishes, and build it up with whole pieces, a pond, a bridge, a
              pet house your pet naps in. Treasure chests hide blueprints for the grander pieces;
              a market stall sells what you grow for treats. Grant every wish and the old Heart
              Tree wakes.
            </p>
            <p>
              Pumpkin Hollow is the first themed garden, free this October: jack-o&apos;-lanterns,
              a hay maze, a scarecrow, friendly ghosts that lead the way to treasure and leave
              sweets after dark, a harvest moon and falling stars, and a pumpkin hat for your pet.
              Also new: a photo with your pet in the camera, a Dynamic Island you can pet, feed
              and play from, pet clocks and calendars for your Home Screen, pictures for every
              item in the shop, and a way to unlink a Minecraft account while keeping everything.
              And Petlings now lives on VanillaTown as well as RevivalSMP.
            </p>
            <a
              className="about-changelog-link"
              href="https://modrinth.com/mod/petlings/versions"
              target="_blank"
              rel="noopener noreferrer"
            >
              See full release notes on Modrinth
            </a>
          </div>
        </div>
      </section>

      {/* How to install */}
      <section className="about-install">
        <div className="container">
          <h2 className="section-title">Get started in 3 steps</h2>

          <div className="about-steps">
            <div className="about-step">
              <div className="about-step-number">1</div>
              <div className="about-step-content">
                <h3>Install the mod</h3>
                <p>
                  Download Petlings from{' '}
                  <a href="https://modrinth.com/mod/petlings" target="_blank" rel="noopener noreferrer">Modrinth</a>
                  {' '}or{' '}
                  <a href="https://www.curseforge.com/minecraft/mc-mods/petlings" target="_blank" rel="noopener noreferrer">CurseForge</a>.
                  You'll need Fabric Loader 0.16+ for Minecraft 1.21.11 or 26.1. Fabric API and
                  GeckoLib are bundled with the jar, so just drop it in your mods folder.
                </p>
              </div>
            </div>
            <div className="about-step">
              <div className="about-step-number">2</div>
              <div className="about-step-content">
                <h3>Adopt your first pet</h3>
                <p>
                  Press O to open the pet menu. Browse the catalog, pick your companion,
                  give them a name and traits. They'll spawn right beside you.
                </p>
              </div>
            </div>
            <div className="about-step">
              <div className="about-step-number">3</div>
              <div className="about-step-content">
                <h3>Explore together</h3>
                <p>
                  Your pet follows you everywhere. Feed them (hunger decays over time), dress them
                  up with cosmetics, chat with them using K, and watch them level up as you play.
                  Manage your pets from petlings.gg between sessions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Keybinds reference */}
      <section className="about-keybinds">
        <div className="container">
          <h3 className="about-keybinds-title">Default keybinds (all rebindable)</h3>
          <div className="keybind-grid">
            <div className="keybind"><kbd>O</kbd><span>Pet Menu</span></div>
            <div className="keybind"><kbd>K</kbd><span>Quick Chat</span></div>
            <div className="keybind"><kbd>Y</kbd><span>Emote Wheel</span></div>
            <div className="keybind"><kbd>J</kbd><span>Toggle Visibility</span></div>
            <div className="keybind"><kbd>B</kbd><span>Dismiss / Re-summon</span></div>
            <div className="keybind"><kbd>\</kbd><span>Toggle HUD</span></div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta">
        <div className="container about-cta-inner">
          <PetlingsLogo size={40} color="var(--text-on-dark)" />
          <h2>Ready to meet your companion?</h2>
          <p>Browse the catalog, pick your pet, and start your adventure.</p>
          <div className="about-cta-actions">
            <Link to="/catalog" className="btn btn-primary btn-lg">Browse Pets</Link>
            <DownloadDropdown className="btn btn-ghost about-cta-ghost btn-lg">
              Download Mod
            </DownloadDropdown>
          </div>
          <div className="about-cta-community">
            <a href="https://discord.gg/hkcNBCTckG" target="_blank" rel="noopener noreferrer" className="about-cta-link">Join the Discord</a>
            <span className="about-cta-dot" aria-hidden="true">.</span>
            <a href="https://github.com/Bownlux/Petlings" target="_blank" rel="noopener noreferrer" className="about-cta-link">Source on GitHub</a>
            <span className="about-cta-dot" aria-hidden="true">.</span>
            <a href="https://modrinth.com/mod/petlings" target="_blank" rel="noopener noreferrer" className="about-cta-link">Modrinth page</a>
          </div>
          <p className="about-disclaimer">
            Not an official Minecraft product. Not approved by or associated with Mojang or Microsoft.
          </p>
        </div>
      </section>
    </div>
  );
}
